import React, { useState, useRef, useMemo } from 'react';

// Controlled recipient "To" field — a chip/token input (E4 Layer 1).
//
// Behavior (standard email-client):
//  - Typeahead over `suggestions` (built from the student roster). A query
//    matches on student name OR family name (and guardian name/email), and
//    selecting a suggestion adds ALL of that family's guardian-email chips.
//  - Manual entry: typing a raw address and pressing comma/Enter stages it as
//    a chip (basic email-format validation). Multiple comma-separated allowed.
//  - Every chip is removable via its × or Backspace when the input is empty.
//  - Addresses are de-duped case-insensitively.
//
// Props:
//   recipients  [{ email, name }]   current chips (controlled)
//   onChange    (next) => void       called with the next recipients array
//   suggestions [{ id, studentName, familyName, guardians:[{name,email}] }]
//   disabled    bool

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isValidEmail = (e) => EMAIL_RE.test(String(e || '').trim());

const RecipientChips = ({ recipients = [], onChange, suggestions = [], disabled = false }) => {
	const [input, setInput] = useState('');
	const [inputError, setInputError] = useState('');
	const [highlight, setHighlight] = useState(0);
	const [focused, setFocused] = useState(false);
	const inputRef = useRef(null);

	const existing = useMemo(
		() => new Set(recipients.map((r) => r.email.toLowerCase())),
		[recipients]
	);

	// Merge new {name,email} entries, skipping blanks/invalids/dupes.
	const addRecipients = (entries) => {
		const seen = new Set(existing);
		const additions = [];
		entries.forEach((entry) => {
			const email = String(entry.email || '').trim().toLowerCase();
			if (!email || !isValidEmail(email) || seen.has(email)) return;
			seen.add(email);
			additions.push({ email, name: (entry.name || '').trim() });
		});
		if (additions.length) onChange([...recipients, ...additions]);
	};

	const removeRecipient = (email) => {
		onChange(recipients.filter((r) => r.email.toLowerCase() !== email.toLowerCase()));
	};

	const matches = useMemo(() => {
		const q = input.trim().toLowerCase();
		if (!q) return [];
		return suggestions
			.filter((s) => {
				if (s.studentName?.toLowerCase().includes(q)) return true;
				if (s.familyName?.toLowerCase().includes(q)) return true;
				return s.guardians?.some(
					(g) =>
						g.name?.toLowerCase().includes(q) ||
						g.email?.toLowerCase().includes(q)
				);
			})
			.slice(0, 8);
	}, [input, suggestions]);

	const showDropdown = focused && matches.length > 0;

	const pickSuggestion = (s) => {
		addRecipients(s.guardians || []);
		setInput('');
		setInputError('');
		setHighlight(0);
		inputRef.current?.focus();
	};

	// Stage whatever is in the input as a manual chip (comma/Enter with no
	// highlighted suggestion). Returns true if it consumed the input.
	const stageManual = () => {
		const raw = input.replace(/,+$/, '').trim();
		if (!raw) return false;
		if (!isValidEmail(raw)) {
			setInputError(`"${raw}" is not a valid email address.`);
			return false;
		}
		addRecipients([{ email: raw, name: '' }]);
		setInput('');
		setInputError('');
		return true;
	};

	const handleKeyDown = (e) => {
		if (e.key === 'ArrowDown' && showDropdown) {
			e.preventDefault();
			setHighlight((h) => Math.min(h + 1, matches.length - 1));
			return;
		}
		if (e.key === 'ArrowUp' && showDropdown) {
			e.preventDefault();
			setHighlight((h) => Math.max(h - 1, 0));
			return;
		}
		if (e.key === 'Escape') {
			setFocused(false);
			return;
		}
		if (e.key === ',') {
			// Comma always stages a manual address.
			e.preventDefault();
			stageManual();
			return;
		}
		if (e.key === 'Enter') {
			e.preventDefault();
			if (showDropdown && matches[highlight]) {
				pickSuggestion(matches[highlight]);
			} else {
				stageManual();
			}
			return;
		}
		if (e.key === 'Backspace' && input === '' && recipients.length > 0) {
			e.preventDefault();
			removeRecipient(recipients[recipients.length - 1].email);
		}
	};

	const handleChange = (e) => {
		setInput(e.target.value);
		setInputError('');
		setHighlight(0);
	};

	const handleBlur = () => {
		// Delay so a suggestion click registers before the dropdown unmounts.
		setTimeout(() => setFocused(false), 120);
	};

	return (
		<div className="rc-field">
			<div
				className={`rc-box${disabled ? ' is-disabled' : ''}`}
				onClick={() => inputRef.current?.focus()}>
				{recipients.map((r) => (
					<span className="rc-chip" key={r.email}>
						<span className="rc-chip__label" title={r.email}>
							{r.name ? `${r.name}` : r.email}
						</span>
						{!disabled && (
							<button
								type="button"
								className="rc-chip__remove"
								aria-label={`Remove ${r.name || r.email}`}
								onMouseDown={(e) => e.preventDefault()}
								onClick={() => removeRecipient(r.email)}>
								<i className="fa-sharp fa-solid fa-xmark" />
							</button>
						)}
					</span>
				))}
				<input
					ref={inputRef}
					type="text"
					className="rc-input"
					value={input}
					onChange={handleChange}
					onKeyDown={handleKeyDown}
					onFocus={() => setFocused(true)}
					onBlur={handleBlur}
					placeholder={recipients.length === 0 ? 'Type a name or email…' : ''}
					disabled={disabled}
					autoComplete="off"
				/>

				{showDropdown && (
					<ul className="rc-dropdown" role="listbox">
						{matches.map((s, idx) => (
							<li
								key={s.id}
								role="option"
								aria-selected={idx === highlight}
								className={`rc-option${idx === highlight ? ' is-active' : ''}`}
								onMouseEnter={() => setHighlight(idx)}
								onMouseDown={(e) => e.preventDefault()}
								onClick={() => pickSuggestion(s)}>
								<span className="rc-option__name">
									<i className="fa-sharp fa-solid fa-user-group rc-option__icon" />
									{s.studentName}
								</span>
								<span className="rc-option__emails">
									{(s.guardians || []).length === 0
										? 'No email on file'
										: s.guardians.map((g) => g.email).join(', ')}
								</span>
							</li>
						))}
					</ul>
				)}
			</div>
			{inputError && <div className="rc-error">{inputError}</div>}
		</div>
	);
};

export default RecipientChips;
